import React, { useState, useEffect } from 'react';
import './AddSubGoalModal.css';
import { MAX_SUBGOAL_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH } from '../constants';
import { useTranslation } from '../i18n';
import Icon from './Icon';
import Modal from './Modal';
import { logger } from '../utils/logger';

interface AddSubGoalModalProps {
  isOpen: boolean;
  goalTitle: string;
  onAdd: (title: string, description: string) => Promise<void> | void;
  onCancel: () => void;
}

const AddSubGoalModal: React.FC<AddSubGoalModalProps> = ({
  isOpen,
  goalTitle,
  onAdd,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setErrors({});
      setIsLoading(false);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: { title?: string; description?: string } = {};
    
    if (!title.trim()) {
  newErrors.title = t('subgoals.validation.titleRequired');
    } else if (title.trim().length < 3) {
  newErrors.title = t('subgoals.validation.titleMinLength', { min: 3 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onAdd(title.trim(), description.trim());
    } catch (error) {
      logger.error('Error adding subgoal', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isLoading) return;
    setTitle('');
    setDescription('');
    setErrors({});
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <Modal
      bare
      isOpen={isOpen}
      onClose={handleCancel}
      disableClose={isLoading}
      className="add-subgoal-modal-overlay"
      contentClassName="add-subgoal-modal"
    >
      <div className="modal-header">
        <div>
          <h3 className="modal-title">
            <span className="modal-icon"><Icon name="clipboard" /></span>
            {t('subgoals.addSubGoal')}
          </h3>
          <p className="modal-subtitle">
            {t('subgoals.mainGoal')}: <strong>{goalTitle}</strong>
          </p>
        </div>
        <button 
          onClick={handleCancel}
          className="modal-close-btn"
          title={t('common.close')}
          disabled={isLoading}
        >
          <Icon name="close" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-group">
          <label htmlFor="subgoal-title" className="form-label">
            {t('subgoals.subGoalTitle')} *
          </label>
          <input
            id="subgoal-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('subgoals.titlePlaceholder')}
            className={`form-input ${errors.title ? 'error' : ''}`}
            autoFocus
            maxLength={MAX_SUBGOAL_TITLE_LENGTH}
            disabled={isLoading}
          />
          {errors.title && (
            <span className="error-message">{errors.title}</span>
          )}
          <div className="character-count">
            {title.length}/{MAX_SUBGOAL_TITLE_LENGTH}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="subgoal-description" className="form-label">
            {t('subgoals.subGoalDescription')}
          </label>
          <textarea
            id="subgoal-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('subgoals.descriptionPlaceholderOptional')}
            className={`form-textarea ${errors.description ? 'error' : ''}`}
            rows={4}
            maxLength={MAX_DESCRIPTION_LENGTH}
            disabled={isLoading}
          />
          {errors.description && (
            <span className="error-message">{errors.description}</span>
          )}
          <div className="character-count">
            {description.length}/500
          </div>
        </div>

        <div className="modal-actions">
          <button 
            type="button" 
            onClick={handleCancel}
            className="btn-cancel"
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button 
            type="submit"
            className="btn-submit"
            disabled={!title.trim() || isLoading}
          >
            {isLoading ? (
              <span className="btn-spinner" />
            ) : (
              <>
                <span className="btn-icon"><Icon name="plus" /></span>
                {t('subgoals.addSubGoal')}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSubGoalModal;
